import { forwardRef, useEffect, useState } from "react";
import { cn } from "../lib/cn";

/**
 * Collapse — manifest §Collapse.
 *
 *   <Collapse in={expanded} timeoutMs={300} unmountOnExit>
 *     <div>…</div>
 *   </Collapse>
 *
 * CSS grid-rows animation: the wrapper animates grid-template-rows between
 * 0fr and 1fr while an inner overflow-hidden row clips the content — no
 * height measuring, works with dynamic content. MUI's `in` prop name is
 * preserved. Duration is a runtime value so it is set via inline style.
 */
export interface CollapseProps {
  /** MUI-compatible prop name — expanded when true. */
  in: boolean;
  /** Transition duration in ms. */
  timeoutMs?: number;
  /** Unmount the content once the exit transition finishes. */
  unmountOnExit?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
  ({ in: expanded, timeoutMs = 300, unmountOnExit = false, children, className }, ref) => {
    const [mounted, setMounted] = useState(expanded || !unmountOnExit);

    useEffect(() => {
      if (expanded) {
        setMounted(true);
        return;
      }
      if (!unmountOnExit) {
        return;
      }
      const timer = window.setTimeout(() => {
        setMounted(false);
      }, timeoutMs);
      return () => {
        window.clearTimeout(timer);
      };
    }, [expanded, unmountOnExit, timeoutMs]);

    if (!mounted) {
      return null;
    }

    return (
      <div
        ref={ref}
        inert={!expanded}
        aria-hidden={!expanded || undefined}
        className={cn(
          "grid transition-[grid-template-rows,opacity] ease-in-out",
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
          className,
        )}
        style={{ transitionDuration: `${timeoutMs}ms` }}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    );
  },
);
Collapse.displayName = "Collapse";
